import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [kind, setKind] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setKind(r.data.kind);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired" || attempts.current > 8) { setStatus("error"); return; }
      } catch { if (attempts.current > 8) { setStatus("error"); return; } }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  const dest = kind === "booking" ? "/dashboard" : "/provider";

  return (
    <Layout hideConcierge>
      <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="payment-success-page">
        {status === "checking" && (<><Loader2 className="w-14 h-14 text-lawn animate-spin mx-auto" /><h1 className="font-display text-2xl font-extrabold text-lawn-ink mt-6">Confirming your payment…</h1><p className="text-muted-foreground mt-2">Please wait a moment.</p></>)}
        {status === "paid" && (<><div className="w-16 h-16 rounded-full bg-lawn-light flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-lawn" /></div><h1 className="font-display text-3xl font-extrabold text-lawn-ink mt-6">Payment successful! 🎉</h1><p className="text-muted-foreground mt-2">{kind === "subscription" ? "Your plan is now active." : kind === "placement" ? "Your placement boost is live." : "Your booking is confirmed and scheduled."}</p><Button data-testid="success-continue-btn" onClick={() => navigate(dest)} className="mt-6 rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Go to Dashboard</Button></>)}
        {status === "error" && (<><XCircle className="w-14 h-14 text-destructive mx-auto" /><h1 className="font-display text-2xl font-extrabold text-lawn-ink mt-6">Something went wrong</h1><p className="text-muted-foreground mt-2">We couldn't confirm your payment. If you were charged, it will reflect shortly.</p><Button onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-full">Back home</Button></>)}
      </div>
    </Layout>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <Layout hideConcierge>
      <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="payment-cancel-page">
        <XCircle className="w-14 h-14 text-gold mx-auto" />
        <h1 className="font-display text-2xl font-extrabold text-lawn-ink mt-6">Payment canceled</h1>
        <p className="text-muted-foreground mt-2">No charge was made. You can try again anytime.</p>
        <Button onClick={() => navigate(-1)} className="mt-6 rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Go back</Button>
      </div>
    </Layout>
  );
}
